import { ActionContext } from "vuex";
import { RootState } from "@tt-app/store/root/types";
import { Alias } from "@tt-app/types";

export interface Session {
  id: number;
  version: string;
  activeDeviceUuid: string;
  limitedMode: {
    id: number;
    version: string;
  };
}

export interface LiveState {
  sessions: Session[];
}

export interface SessionsById {
  [key: string]: Session;
}

export interface SessionDetails {
  group: {
    id: number;
    name: string;
  };
  id: number;
  startsAt: string;
  endsAt: string;
  version: string;
  activeDeviceUuid: string;
}

export interface SessionDetailsById {
  [key: number]: SessionDetails;
}

export interface LimitedModeExercise {
  uuid: string;
  exerciseType: number;
  skill: number;
  courseUuid: string;
  title: string;
  finished: string;
  numQuestions: number;
  bestScore: number;
  lastTimeToCompletion: string;
}

export interface LimitedModeDetails {
  title: string;
  startsAt: string;
  endsAt: string;
  version: string;
  exercises: LimitedModeExercise[];
}

export interface TerratalkLiveState {
  limitedModeDetails?: LimitedModeDetails;
  liveState?: LiveState;
  modals: {
    sessionEnded: {
      open: boolean;
    };
    sessionStarted: {
      open: boolean;
    };
  };
  previousFetcherAlias: Alias;
  sessions?: SessionsById;
  sessionDetails?: SessionDetailsById;
}

export const TerratalkLiveNamespace = "terratalkLive";
export type S = TerratalkLiveState;
export type R = RootState;

/* Mutations */
export enum TerratalkLiveMutationNames {
  HIDE_SESSION_ENDED_MODAL = "HIDE_SESSION_ENDED_MODAL",
  HIDE_SESSION_STARTED_MODAL = "HIDE_SESSION_STARTED_MODAL",
  LIMITED_MODE_DETAILS = "LIMITED_MODE_DETAILS",
  LIVE_STATE = "LIVE_STATE",
  PREVIOUS_FETCHER_ALIAS = "PREVIOUS_FETCHER_ALIAS",
  SESSIONS = "SESSIONS",
  SESSION_DETAILS = "SESSION_DETAILS",
  SHOW_SESSION_ENDED_MODAL = "SHOW_SESSION_ENDED_MODAL",
  SHOW_SESSION_STARTED_MODAL = "SHOW_SESSION_STARTED_MODAL"
}

export interface TerratalkLiveMutations {
  [TerratalkLiveMutationNames.HIDE_SESSION_ENDED_MODAL](state: S): void;
  [TerratalkLiveMutationNames.HIDE_SESSION_STARTED_MODAL](state: S): void;
  [TerratalkLiveMutationNames.LIMITED_MODE_DETAILS](state: S, payload: LimitedModeDetails): void;
  [TerratalkLiveMutationNames.LIVE_STATE](state: S, payload: LiveState): void;
  [TerratalkLiveMutationNames.PREVIOUS_FETCHER_ALIAS](state: S, payload: Alias): void;
  [TerratalkLiveMutationNames.SESSIONS](state: S, payload: SessionsById): void;
  [TerratalkLiveMutationNames.SESSION_DETAILS](
    state: S,
    payload: { [key: number]: SessionDetails }
  ): void;
  [TerratalkLiveMutationNames.SHOW_SESSION_ENDED_MODAL](state: S): void;
  [TerratalkLiveMutationNames.SHOW_SESSION_STARTED_MODAL](state: S): void;
}

/* Getters */
export enum TerratalkLiveGetterNames {
  ACTIVE_SESSION = "ACTIVE_SESSION",
  ACTIVE_SESSION_DETAILS = "ACTIVE_SESSION_DETAILS",
  LIMITED_MODE_DETAILS = "LIMITED_MODE_DETAILS",
  LIMITED_MODE_EXERCISES = "LIMITED_MODE_EXERCISES",
  LIVE_STATE = "LIVE_STATE",
  SESSIONS = "SESSIONS",
  SESSION_DETAILS = "SESSION_DETAILS",
  UPCOMING_SESSIONS = "UPCOMING_SESSIONS"
}

export interface TerratalkLiveGetters {
  [TerratalkLiveGetterNames.ACTIVE_SESSION](state: S, getters: any): undefined | Session;
  [TerratalkLiveGetterNames.ACTIVE_SESSION_DETAILS](state: S): undefined | SessionDetails;
  [TerratalkLiveGetterNames.LIMITED_MODE_DETAILS](state: S): undefined | LimitedModeDetails;
  [TerratalkLiveGetterNames.LIMITED_MODE_EXERCISES](state: S): undefined | LimitedModeExercise[];
  [TerratalkLiveGetterNames.LIVE_STATE](state: S): undefined | LiveState;
  [TerratalkLiveGetterNames.SESSIONS](state: S): undefined | SessionsById;
  [TerratalkLiveGetterNames.SESSION_DETAILS](state: S): undefined | SessionDetailsById;
  [TerratalkLiveGetterNames.UPCOMING_SESSIONS](state: S): undefined | Session[];
}

/* Actions */
export enum TerratalkLiveActionNames {
  HIDE_SESSION_ENDED_MODAL = "HIDE_SESSION_ENDED_MODAL",
  HIDE_SESSION_STARTED_MODAL = "HIDE_SESSION_STARTED_MODAL",
  REQUEST_LIMITED_MODE_DETAILS = "REQUEST_LIMITED_MODE_DETAILS",
  REQUEST_SESSION_DETAILS = "REQUEST_SESSION_DETAILS",
  REQUEST_STATE = "REQUEST_STATE",
  SHOW_SESSION_ENDED_MODAL = "SHOW_SESSION_ENDED_MODAL",
  SHOW_SESSION_STARTED_MODAL = "SHOW_SESSION_STARTED_MODAL",
  TAKE_OWNERSHIP = "TAKE_OWNERSHIP"
}

interface AugmentedActionContext extends Omit<ActionContext<S, R>, "commit"> {
  commit<K extends keyof TerratalkLiveMutations>(
    key: K,
    payload?: Parameters<TerratalkLiveMutations[K]>[1]
  ): ReturnType<TerratalkLiveMutations[K]>;
}

export interface TerratalkLiveActions {
  [TerratalkLiveActionNames.HIDE_SESSION_ENDED_MODAL](
    context: AugmentedActionContext
  ): Promise<void>;
  [TerratalkLiveActionNames.HIDE_SESSION_STARTED_MODAL](
    context: AugmentedActionContext
  ): Promise<void>;
  [TerratalkLiveActionNames.REQUEST_LIMITED_MODE_DETAILS](
    context: AugmentedActionContext
  ): Promise<void>;
  [TerratalkLiveActionNames.REQUEST_STATE](context: AugmentedActionContext): Promise<void>;
  [TerratalkLiveActionNames.REQUEST_SESSION_DETAILS](
    context: AugmentedActionContext
  ): Promise<void>;
  [TerratalkLiveActionNames.SHOW_SESSION_ENDED_MODAL](
    context: AugmentedActionContext
  ): Promise<void>;
  [TerratalkLiveActionNames.SHOW_SESSION_STARTED_MODAL](
    context: AugmentedActionContext
  ): Promise<void>;
  [TerratalkLiveActionNames.TAKE_OWNERSHIP](context: AugmentedActionContext): Promise<void>;
}
